import { useEffect, useMemo, useRef, useState } from "react";
import { faCircleDot, faDashboard, faBox, faList, faUsers, faBuilding, faIdBadge, faUserShield, faSitemap, faAddressBook, faClipboard, faListCheck, faChartColumn } from "@fortawesome/free-solid-svg-icons";
import { MenuItems as fallbackMenuItems } from "./Menu";
import { fetchAndCacheMenuItems, getCachedMenuItems, isMenuCacheFresh, startMenuAutoRefresh } from "../../services/menuCache";
import config from "../../config";
import { getlist } from "../../contexts/apicontexts";
import { showToast } from "../Ui_Element/CustomToast";
import { normalizeTicketData } from "../../pages/Tickets/ticketUtils";

const iconMap = {
  faDashboard,
  faBox,
  faList,
  faUsers,
  faBuilding,
  faIdBadge,
  faUserShield,
  faSitemap,
  faAddressBook,
  faClipboard,
  faListCheck,
  faChartColumn,
};

const resolveIcon = (iconName) => {
  if (!iconName) return faCircleDot;
  const key = String(iconName).trim();
  return iconMap[key] || faCircleDot;
};

const getUserRoleNames = (user) => {
  if (!user) return [];

  const fromArray = Array.isArray(user.roleNames)
    ? user.roleNames
    : Array.isArray(user.RoleNames)
      ? user.RoleNames
      : [];

  if (fromArray.length) {
    return fromArray.map((x) => String(x || "").trim()).filter(Boolean);
  }

  const single = user.roleName || user.RoleName || user.role || user.Role;
  return single ? [String(single).trim()] : [];
};

const normalizeRole = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ");

const isEmployeeUser = (user) => {
  return getUserRoleNames(user).some((role) => normalizeRole(role).includes("employee"));
};

const isCustomerAdminUser = (user) => {
  return getUserRoleNames(user).some((role) => {
    const normalized = normalizeRole(role);
    return normalized === "customer" || normalized === "customer admin" || (normalized.includes("customer") && !normalized.includes("customer user"));
  });
};

const isCustomerScopedUser = (user) => {
  return getUserRoleNames(user).some((role) => {
    const normalized = normalizeRole(role);
    return normalized === "customer" || normalized === "customer user";
  });
};

const isAnalyticsRole = (user) => {
  return getUserRoleNames(user).some((role) => {
    const normalized = normalizeRole(role);
    return normalized.includes("admin")
      || normalized.includes("manager")
      || normalized.includes("support")
      || normalized.includes("help")
      || normalized.includes("hr");
  });
};

const normalizePath = (path) => {
  if (!path) return "";
  const trimmed = String(path).trim();
  if (!trimmed) return "";
  if (trimmed.startsWith("http://") || trimmed.startsWith("https://")) return trimmed;
  return trimmed.startsWith("/") ? trimmed : `/${trimmed}`;
};

const dedupeMenuGroups = (groups) => {
  const result = [];
  const byGroupKey = new Map();

  const groupKeyOf = (g) => String(g?.group || "").trim().toLowerCase();
  const itemKeyOf = (item) => {
    if (!item) return "";
    if (item.isDropdown) return `dropdown:${String(item.label || "").trim().toLowerCase()}`;
    return `to:${String(item.to || "").trim().toLowerCase()}`;
  };

  for (const group of groups || []) {
    const gKey = groupKeyOf(group);
    if (!gKey) continue;

    if (!byGroupKey.has(gKey)) {
      const next = { ...group, items: Array.isArray(group.items) ? [...group.items] : [] };
      byGroupKey.set(gKey, next);
      result.push(next);
      continue;
    }

    const existing = byGroupKey.get(gKey);
    const merged = [];
    const seenItemKeys = new Set();

    const addItem = (item) => {
      const key = itemKeyOf(item);
      if (!key || seenItemKeys.has(key)) return;
      seenItemKeys.add(key);

      if (item?.isDropdown && Array.isArray(item.subItems)) {
        const seenSubs = new Set();
        const subItems = item.subItems.filter((s) => {
          const to = String(s?.to || "").trim().toLowerCase();
          if (!to || seenSubs.has(to)) return false;
          seenSubs.add(to);
          return true;
        });
        merged.push({ ...item, subItems });
        return;
      }

      merged.push(item);
    };

    for (const i of existing.items || []) addItem(i);
    for (const i of group.items || []) addItem(i);
    existing.items = merged;
  }

  return result;
};

const ensureSupportTaskAccess = (groups, user) => {
  const employeeUserId = user?.userId ?? user?.id ?? user?.Id ?? "";
  const employeeLogin = isEmployeeUser(user);
  const customerLogin = isCustomerScopedUser(user);
  const customerAdminLogin = isCustomerAdminUser(user);
  const assignedRoute = employeeLogin && employeeUserId ? `/assigned-tickets?userId=${encodeURIComponent(employeeUserId)}` : "/tickets";
  const taskRoute = employeeLogin
    ? employeeUserId
      ? `/tasks?scope=mine&userId=${encodeURIComponent(employeeUserId)}`
      : "/tasks?scope=mine"
    : "/tasks";
  const customerUserRoute = "/client/customer-users";

  const nextGroups = (groups || []).map((group) => ({
    ...group,
    items: (group.items || [])
      .map((item) => {
        if (customerLogin && item?.to === "/tasks") return null;

        if (item?.to === "/tickets") {
          return { ...item, to: assignedRoute };
        }

        if (item?.to === "/tasks") {
          return { ...item, to: taskRoute };
        }

        if (item?.subItems?.length) {
          return {
            ...item,
            subItems: item.subItems
              .map((subItem) => {
                if (customerLogin && subItem?.to === "/tasks") return null;
                if (subItem?.to === "/tickets") return { ...subItem, to: assignedRoute };
                if (!customerAdminLogin && subItem?.to === customerUserRoute) return null;
                if (subItem?.to === "/tasks") return { ...subItem, to: taskRoute };
                return subItem;
              })
              .filter(Boolean),
          };
        }

        return item;
      })
      .filter(Boolean),
  }));

  return nextGroups;
};

const ensureCustomerUserAccess = (groups, user) => {
  if (!isCustomerAdminUser(user)) return groups || [];

  const targetGroupKey = "client";
  const nextGroups = [...(groups || [])];
  const clientGroup = nextGroups.find((group) => String(group?.group || "").trim().toLowerCase() === targetGroupKey);
  const item = { to: "/client/customer-users", label: "Customer Users", icon: faUsers };

  if (clientGroup) {
    const exists = (clientGroup.items || []).some((entry) => entry?.to === item.to);
    if (!exists) {
      clientGroup.items = [...(clientGroup.items || []), item];
    }
    return nextGroups;
  }

  return [...nextGroups, { group: "Client", items: [item] }];
};

const ensureMyCustomerAccess = (groups, user) => {
  if (!isEmployeeUser(user)) return groups || [];

  const nextGroups = [...(groups || [])];
  const clientGroup = nextGroups.find((group) => String(group?.group || "").trim().toLowerCase() === "client");
  const item = { to: "/client/my-customers", label: "My Customers", icon: faAddressBook };

  if (clientGroup) {
    const exists = (clientGroup.items || []).some((entry) => entry?.to === item.to);
    if (!exists) {
      clientGroup.items = [...(clientGroup.items || []), item];
    }
    return nextGroups;
  }

  return [...nextGroups, { group: "Client", items: [item] }];
};

const ensureAdminAnalyticsAccess = (groups, user) => {
  if (!isAnalyticsRole(user)) return groups || [];

  const nextGroups = [...(groups || [])];
  const supportGroup = nextGroups.find((group) => String(group?.group || "").trim().toLowerCase() === "support");
  const item = { to: "/tickets/reports/admin-analytics", label: "Admin Analytics", icon: faChartColumn };

  if (supportGroup) {
    const exists = (supportGroup.items || []).some((entry) => entry?.to === item.to);
    if (!exists) {
      supportGroup.items = [...(supportGroup.items || []), item];
    }
    return nextGroups;
  }

  return [...nextGroups, { group: "Support", items: [item] }];
};

const applyTicketBadge = (groups, unreadCount) => {
  const badge = unreadCount > 99 ? "99+" : unreadCount > 0 ? String(unreadCount) : "";

  return (groups || []).map((group) => ({
    ...group,
    items: (group.items || []).map((item) => {
      const nextItem = { ...item };
      const isTicketLink = nextItem?.to === "/tickets" || String(nextItem?.to || "").startsWith("/assigned-tickets");

      if (isTicketLink) {
        if (badge) nextItem.badge = badge;
        else delete nextItem.badge;
      }

      if (Array.isArray(nextItem.subItems)) {
        nextItem.subItems = nextItem.subItems.map((subItem) => {
          const nextSubItem = { ...subItem };
          const isSubTicketLink = nextSubItem?.to === "/tickets" || String(nextSubItem?.to || "").startsWith("/assigned-tickets");

          if (isSubTicketLink) {
            if (badge) nextSubItem.badge = badge;
            else delete nextSubItem.badge;
          }

          return nextSubItem;
        });
      }

      return nextItem;
    }),
  }));
};

const getTicketNotificationEndpoint = (user) => {
  const userId = user?.userId ?? user?.id ?? user?.Id ?? "";
  if (!isCustomerScopedUser(user)) {
    const query = userId ? `?userId=${encodeURIComponent(userId)}` : "";
    return `${config.baseurl}Ticket/getassignedlist${query}`;
  }

  return `${config.baseurl}Ticket/getlist`;
};

export default function useUserMenuItems() {
  const [baseMenuItems, setBaseMenuItems] = useState(fallbackMenuItems);
  const [ticketUnreadCount, setTicketUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const previousUnreadRef = useRef(null);
  const menuItems = useMemo(() => applyTicketBadge(baseMenuItems, ticketUnreadCount), [baseMenuItems, ticketUnreadCount]);

  useEffect(() => {
    const token = window.localStorage.getItem("authtoken");
    const userRaw = window.localStorage.getItem("user");
    let userObj = null;

    try {
      userObj = userRaw ? JSON.parse(userRaw) : null;
    } catch {
      userObj = null;
    }
    // Backend/login stores `userId`; keep fallbacks for older shapes.
    const userId = userObj?.userId ?? userObj?.id ?? userObj?.Id ?? null;

    if (!token || !userId) {
      setBaseMenuItems(fallbackMenuItems);
      return;
    }

    let cancelled = false;

    const applyFromApiResponse = (apiData) => {
      // Backend returns: { menus: MenuItemContract[], status: ResponseStatus }
      const groups = (apiData?.menus || [])
        .filter((m) => m?.type === "group")
        .sort((a, b) => Number(a.sortOrder || 0) - Number(b.sortOrder || 0))
        .map((g) => {
          const items = (g.children || [])
            .sort((a, b) => Number(a.sortOrder || 0) - Number(b.sortOrder || 0))
            .map((c) => {
              const isDropdown = c.type === "collapse";
              if (isDropdown) {
                return {
                  label: c.title,
                  icon: resolveIcon(c.icon),
                  isDropdown: true,
                  subItems: (c.children || [])
                    .sort((a, b) => Number(a.sortOrder || 0) - Number(b.sortOrder || 0))
                    .map((x) => ({
                      to: normalizePath(x.url),
                      label: x.title,
                      icon: resolveIcon(x.icon),
                    }))
                    .filter((x) => x.to),
                };
              }
              return {
                to: normalizePath(c.url),
                label: c.title,
                icon: resolveIcon(c.icon),
              };
            })
            .filter((x) => x.to || (x.isDropdown && x.subItems?.length));
          return { group: g.title || g.id, items };
        })
        .filter((g) => g.items.length);

      const finalMenu = dedupeMenuGroups(groups);
      return ensureAdminAnalyticsAccess(
        ensureMyCustomerAccess(
          ensureCustomerUserAccess(ensureSupportTaskAccess(finalMenu, userObj), userObj),
          userObj
        ),
        userObj
      );
    };

    const load = async () => {
      setLoading(true);
      try {
        // 1) Use cache immediately if present
        const cached = getCachedMenuItems();
        if (!cancelled && cached) setBaseMenuItems(applyFromApiResponse(cached));

        // 2) Refresh from API if cache is missing/stale
        if (!isMenuCacheFresh()) {
          const data = await fetchAndCacheMenuItems(userId);
          if (!cancelled && data) setBaseMenuItems(applyFromApiResponse(data));
        }
      } catch (e) {
        if (!cancelled) {
          setBaseMenuItems(
            ensureAdminAnalyticsAccess(
              ensureMyCustomerAccess(
                ensureCustomerUserAccess(ensureSupportTaskAccess(fallbackMenuItems, userObj), userObj),
                userObj
              ),
              userObj
            )
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();

    const stop = startMenuAutoRefresh(userId, (data) => {
      if (!cancelled && data) setBaseMenuItems(applyFromApiResponse(data));
    });

    return () => {
      cancelled = true;
      stop();
    };
  }, []);

  useEffect(() => {
    const token = window.localStorage.getItem("authtoken");
    const userRaw = window.localStorage.getItem("user");

    if (!token || !userRaw) {
      setTicketUnreadCount(0);
      previousUnreadRef.current = null;
      return;
    }

    let userObj = null;

    try {
      userObj = JSON.parse(userRaw);
    } catch {
      userObj = null;
    }

    const userId = userObj?.userId ?? userObj?.id ?? userObj?.Id ?? "";
    if (!userObj || !userId) {
      setTicketUnreadCount(0);
      previousUnreadRef.current = null;
      return;
    }

    let cancelled = false;

    const loadUnreadCount = async () => {
      try {
        const response = await getlist(getTicketNotificationEndpoint(userObj));
        if (cancelled || response?.status !== 200) return;

        const unreadCount = (Array.isArray(response.data) ? response.data : [])
          .map(normalizeTicketData)
          .reduce((sum, ticket) => sum + Number(ticket.unreadCount || 0), 0);

        setTicketUnreadCount(unreadCount);

        if (previousUnreadRef.current !== null && unreadCount > previousUnreadRef.current) {
          showToast.info(
            unreadCount === 1
              ? "You have 1 unread ticket update."
              : `You have ${unreadCount} unread ticket updates.`
          );
        }

        previousUnreadRef.current = unreadCount;
      } catch {
        if (!cancelled) {
          setTicketUnreadCount(0);
        }
      }
    };

    loadUnreadCount();
    const intervalId = window.setInterval(loadUnreadCount, 45000);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, []);

  return { menuItems, loading };
}
